import { observable, action } from 'mobx';
import InvoiceFormStore from '../../domain/entities/InvoiceFormStore/InvoiceFormStore';
import CompanyBillingDetailsFormStore from '../../domain/entities/CompanyBillingDetailsFormStore/CompanyBillingDetailsFormStore';
import InvoiceDetails from '../../domain/entities/InvoiceDetails';
import CompanyBillingDetails from '../../domain/entities/CompanyBillingDetails';
import { FIELD_TYPE, INVOICE_DONATE_MARKS } from '../../constants/Constants';
import Api from '../../api';

interface IInvoiceRepositoryConfig {
  api: Api;
}

class InvoiceRepository {
  @observable public isUpdating = false;

  @observable public invoiceFormStore: InvoiceFormStore;

  @observable public companyBillingFormStore: CompanyBillingDetailsFormStore;

  public api: Api;

  constructor(config: IInvoiceRepositoryConfig) {
    this.invoiceFormStore = new InvoiceFormStore();
    this.api = config.api;
  }

  @action
  private setIsUpdating = (isUpdating: boolean): void => {
    this.isUpdating = isUpdating;
  };

  @action
  async updateInvoiceDetails(
    companyBillingDetails: CompanyBillingDetails,
  ): Promise<InvoiceDetails> {
    this.setIsUpdating(true);

    const {
      [FIELD_TYPE.COMPANY_NAME]: companyName,
      [FIELD_TYPE.COMPANY_TAX_NUMBER]: companyTaxNumber,
    } = companyBillingDetails.toJSON();
    const invoiceDetails = this.invoiceFormStore.buildInvoiceDetails({
      [FIELD_TYPE.CORPORATE_NAME]: companyName,
      [FIELD_TYPE.CORPORATE_TAX_ID]: companyTaxNumber,
    });

    const updatedInvoiceDetails = await this.api.updateInvoiceDetails(
      invoiceDetails.toJSON(),
    );
    this.setIsUpdating(false);

    return InvoiceDetails.fromJSON(updatedInvoiceDetails);
  }

  @action
  public setCompanyBillingDetailsFormStore = (
    formStore: CompanyBillingDetailsFormStore,
  ): void => {
    this.companyBillingFormStore = formStore;
  };

  @action
  public setInvoiceDetails = (
    invoiceDetails: InvoiceDetails,
  ): InvoiceRepository => {
    const {
      [FIELD_TYPE.INVOICE_CARRIER_ID]: carrierId,
      [FIELD_TYPE.INVOICE_CARRIER_TYPE]: carrierType,
      [FIELD_TYPE.INVOICE_DONATE_MARK]: donateMark,
      [FIELD_TYPE.INVOICE_DONATION_LOVE_CODE]: donationLoveCode,
    } = invoiceDetails.toJSON();

    this.invoiceFormStore.setValues({
      [FIELD_TYPE.INVOICE_CARRIER_ID]: carrierId,
      [FIELD_TYPE.INVOICE_CARRIER_TYPE]: carrierType,
      [FIELD_TYPE.INVOICE_DONATE_MARK]: donateMark,
      [FIELD_TYPE.INVOICE_DONATION_LOVE_CODE]: donationLoveCode,
    });

    return this;
  };

  public validateInvoiceDetails(): boolean {
    const {
      values: { [FIELD_TYPE.INVOICE_DONATE_MARK]: donateMark },
    } = this.invoiceFormStore;
    let isValid: boolean;
    if (donateMark === INVOICE_DONATE_MARKS.PAPER_INVOICE) {
      isValid = this.companyBillingFormStore.isValid;
      this.companyBillingFormStore.setShowErrors(!isValid);
    } else {
      isValid = this.invoiceFormStore.validate();
      this.invoiceFormStore.setShowErrors(!isValid);
    }

    return isValid;
  }
}

export default InvoiceRepository;
